import { KeyRound, Mail } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { formatDate } from '@/lib/dates';
import { useAddPasskey, useCanUsePasskeys, usePasskeys, useRemovePasskey } from '@/lib/passkeys';
import { Button, Card, ErrorText } from '@/components/ui';

/** How you sign in: passkeys, with email codes always available as a fallback. */
export function AccountSecurityPage() {
  const { user } = useAuth();
  return (
    <div className="space-y-6">
      <Passkeys />
      <Card title="Email codes">
        <div className="flex items-start gap-3">
          <Mail className="mt-0.5 size-4 text-muted-foreground" aria-hidden />
          <p className="text-sm text-muted-foreground">
            You can always sign in with a one-time code sent to{' '}
            <span className="font-medium text-foreground">{user?.email}</span>. Nirvik Ready never
            uses passwords.
          </p>
        </div>
      </Card>
    </div>
  );
}

/** Lists, adds and removes passkeys (Cognito WebAuthn credentials). */
function Passkeys() {
  const { mode } = useAuth();
  const canUse = useCanUsePasskeys();
  const passkeys = usePasskeys();
  const add = useAddPasskey();
  const remove = useRemovePasskey();
  const list = passkeys.data ?? [];

  return (
    <Card title="Passkeys">
      <p className="mb-4 text-sm text-muted-foreground">
        Sign in with your fingerprint, face or device PIN instead of waiting for an email code.
        Passkeys are faster and can&rsquo;t be phished.
      </p>
      {mode !== 'cognito' ? (
        <p className="text-sm text-muted-foreground">
          Passkeys are available once Cognito is configured.
        </p>
      ) : (
        <>
          {passkeys.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : list.length === 0 ? (
            <p className="text-sm text-muted-foreground">You haven&rsquo;t added a passkey yet.</p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {list.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <KeyRound className="size-4 text-primary" aria-hidden />
                    <div>
                      <p className="text-sm font-medium">{p.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {p.attachment === 'cross-platform' ? 'Security key or phone' : 'Device'}
                        {p.createdAt && ` · Added ${formatDate(p.createdAt)}`}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Remove the passkey "${p.name}"? You can still sign in with an email code.`,
                        )
                      )
                        remove.mutate(p.id);
                    }}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-4">
            {canUse ? (
              <Button disabled={add.isPending} onClick={() => add.mutate()}>
                <KeyRound className="size-4" aria-hidden />
                {list.length ? 'Add another passkey' : 'Add a passkey'}
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">
                This device can&rsquo;t create passkeys. Try your phone or a computer with a
                fingerprint reader, face recognition or a device PIN.
              </p>
            )}
          </div>
          {add.isSuccess && (
            <p className="mt-3 text-sm text-muted-foreground">
              Passkey added. Next time, choose &ldquo;Sign in with a passkey&rdquo;.
            </p>
          )}
          <ErrorText error={add.error ?? remove.error ?? passkeys.error} />
        </>
      )}
    </Card>
  );
}
